import api from './api';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

/** Strip CRLF characters to prevent log forging (CWE-117) */
function sanitizeForLog(input: string): string {
  return input.replace(/[\r\n]+/g, ' ');
}

export interface SearchResult {
  id: string;
  type: 'incident' | 'user' | 'threat' | 'investigation' | 'activity' | 'protectedPerson' | 'subject';
  title: string;
  description?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export interface SearchResults {
  incidents: SearchResult[];
  users: SearchResult[];
  threats: SearchResult[];
  investigations: SearchResult[];
  activities: SearchResult[];
  protectedPersons: SearchResult[];
  subjects: SearchResult[];
}

export interface SearchCounts {
  incidents: number;
  users: number;
  threats: number;
  investigations: number;
  activities: number;
  protectedPersons: number;
  subjects: number;
  total: number;
}

export interface SearchResponse {
  query: string;
  results: SearchResults;
  counts: SearchCounts;
}

export interface SearchOptions {
  query: string;
  types?: string[]; // Filter by types: 'incidents', 'users', 'threats', 'investigations', 'activities', 'protectedPersons'
  limit?: number; // Results per category (max 50)
}

/**
 * Global search service for searching across all entities
 */
class SearchService {
  /**
   * Perform a global search across all entity types
   */
  async search(options: SearchOptions): Promise<SearchResponse> {
    const { query, types, limit = 10 } = options;

    if (!query || query.trim().length === 0) {
      throw new Error('Search query cannot be empty');
    }

    const params = new URLSearchParams({
      q: query.trim(),
      ...(types && types.length > 0 && { types: types.join(',') }),
      ...(limit && { limit: limit.toString() }),
    });

    try {
      const response = await api.get(`/api/v1/search?${params.toString()}`);

      // Ensure we have valid data
      if (!response.data) {
        console.error('Invalid search response:', sanitizeForLog(JSON.stringify(response)));
        throw new Error('Invalid search response from server');
      }

      return response.data as SearchResponse;
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  /**
   * Search only incidents
   */
  async searchIncidents(query: string, limit = 10): Promise<SearchResult[]> {
    const response = await this.search({ query, types: ['incidents'], limit });
    return response.results.incidents;
  }

  /**
   * Search only users
   */
  async searchUsers(query: string, limit = 10): Promise<SearchResult[]> {
    const response = await this.search({ query, types: ['users'], limit });
    return response.results.users;
  }

  /**
   * Search only threats
   */
  async searchThreats(query: string, limit = 10): Promise<SearchResult[]> {
    const response = await this.search({ query, types: ['threats'], limit });
    return response.results.threats;
  }

  /**
   * Search only investigations
   */
  async searchInvestigations(query: string, limit = 10): Promise<SearchResult[]> {
    const response = await this.search({ query, types: ['investigations'], limit });
    return response.results.investigations;
  }

  /**
   * Search only missions
   */
  async searchMissions(query: string, limit = 10): Promise<SearchResult[]> {
    const response = await this.search({ query, types: ['activities'], limit });
    return response.results.activities;
  }

  /**
   * Search only protected persons
   */
  async searchProtectedPersons(query: string, limit = 10): Promise<SearchResult[]> {
    const response = await this.search({ query, types: ['protectedPersons'], limit });
    return response.results.protectedPersons;
  }

  /**
   * Search only subjects
   */
  async searchSubjects(query: string, limit = 10): Promise<SearchResult[]> {
    const response = await this.search({ query, types: ['subjects'], limit });
    return response.results.subjects;
  }
}

export const searchService = new SearchService();
